import {
  layoutTokens,
  typographyTokens,
  surfaceTokens,
  boardTokens,
  ALERT_RED,
  INFO_BLUE,
  WARNING_GOLD,
} from "@/shared/theme";
import { alpha } from "@mui/material/styles";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BoardMoveControls, useBoardNotice } from "@/features/board";
import { BoardFrame } from "@/shared/components/chessboard/BoardFrame";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRememberedPageState } from "@/features/preferences/page-state";
import {
  chessArrow,
  type ChessArrow as Arrow,
  type ChessSquareStyles as CustomSquareStyles,
  type ChessSquare as Square,
  useEvaluationGutterWidth,
} from "@/shared/components/chessboard";
import { collectTeachingFacts } from "@/features/board/review/teaching-explainer";
import { ChesslaBoardSurface } from "@/features/board/surface";
import { mergeRelationshipEvidence } from "@/features/board/relationship-evidence";
import { boardPositionMarkers } from "@/features/board/review/position-markers";
import { dedupeArrows } from "@/features/board/review/arrow-geometry";
import {
  evaluationForPlayer,
  evaluationScoreForPlayer,
  evidenceSwingText,
  explainBetterPlan,
  explainLineStep,
  lineScoreForPlayer,
  replayLine,
  recordedPatternLine,
  precedingPatternMove,
} from "../pattern-evidence";
import type { PatternExample, PrinciplePatternExample } from "../patterns.api";
import { MicroLabel } from "./PatternTypography";
import {
  useBoardPreferences,
  REVIEW_EVALUATION_DEPTH,
  ReviewBoardSettingsDialog,
  type ReviewBoardSettingsPatch,
} from "@/features/board";
import { DEFAULT_REVIEW_BOARD_SETTINGS, type ReviewBoardSettings } from "@/features/board/review-board-settings";
import {
  canPlayerMove,
  legalTargetsFrom,
  temporarySquareStyles,
  TemporaryPlayRail,
  useTemporaryPlay,
  type TemporaryMoveGrade,
} from "@/features/board/temporary-play";
import { MotifBadge, PrincipleGlyph, ReviewEvidenceGlyph } from "@/shared/components";
import { normalizeEvaluation } from "@/shared/components/evaluation";
import { patternExamplePly } from "../pattern-dossier";
import {
  applySanMove,
  formatEvaluation,
  principlePresentation,
  type AppliedMove,
  type PrincipleSignal,
} from "@chessla/shared";
import { boardEvidenceMotifAnnotation } from "@/features/games/board/BoardMotifBadge";
import { BoardEvidenceBadges } from "@/features/games/board/BoardEvidenceBadges";
import { temporaryMoveEvidence } from "@/features/games/board/temporary-move-evidence";
import { BoardEvidenceWheel, type BoardEvidenceItem } from "@/shared/components/chessboard/BoardEvidenceWheel";
import { principleMarkers } from "@/features/games/board/principle-markers";
import { PrincipleSignalBadge } from "@/features/games/components/PrincipleSignalBadge";
import { formatMotif } from "../format";
import { useNavigate } from "react-router-dom";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveNikolaAction } from "@/features/entitlements/entitlement-policy";
import { useEntitlementCta } from "@/features/entitlements/use-entitlement-cta";
import { fetchGameAnalysis, type AnalyzedMoveResult } from "@/shared/api/engine.api";
import { requestPatternExplanation } from "../pattern-explanation";
import { createVerifiedLine, type VerifiedLine } from "@/features/board/review/verified-line";
import { useProgressiveEvaluation } from "@/features/board/review/use-progressive-evaluation";
import { bookMoveReference, deriveVerifiedBookMotifs, lookupBookMove } from "@/features/openings";
import { OpeningReference } from "@/features/openings/reference";
import { playChessMoveSound } from "@/shared/utils/chess-sound";

export const PATTERN_MOVE_ARROW_COLOR = ALERT_RED.main;

interface PatternEvidenceLessonProps {
  readonly format: "bullet" | "blitz" | "rapid" | "daily";
  readonly exactTimeControl?: string;
  readonly motif: string;
  readonly description: string;
  readonly habit: string;
  readonly occurrences: number;
  readonly affectedGames: number;
  readonly examples: PatternExample[];
  readonly principleExample?: PrinciplePatternExample;
  readonly onReview: (example: PatternExample) => void;
  /** Width at which the board and explanation sit side by side; narrow panels wait for `xl`. */
  readonly splitFrom?: "md" | "xl";
  /** Caps the board column so the whole board fits a fixed-height panel. */
  readonly boardMaxWidth?: string;
}

const squarePattern = /^[a-h][1-8]$/;
const scoreText = (score: ReturnType<typeof evaluationScoreForPlayer>) => (score ? formatEvaluation(score) : "—");
const supportedMarker =
  /^(?:attacker|source|from|target|targets|victim|capture|payoff|controlSquare|support|supportingPiece|pin|pinnedPiece|forcingMoveFrom|forcingMoveTo|defender|defenderFrom|defenderTo|firstTarget|secondTarget|defendedTargets)$/i;
export const evidenceSquareStyles = (evidence?: Record<string, unknown>): CustomSquareStyles => {
  if (!evidence) return {};
  const styles: CustomSquareStyles = {};
  for (const [reason, raw] of Object.entries(evidence)) {
    if (!supportedMarker.test(reason)) continue;
    const tint = /pin/i.test(reason)
      ? alpha(boardTokens.pin, 0.26)
      : /target|victim|capture|payoff/i.test(reason)
        ? alpha(WARNING_GOLD.main, 0.28)
        : alpha(INFO_BLUE.main, 0.22);
    for (const value of Array.isArray(raw) ? raw : [raw]) {
      if (typeof value === "string" && squarePattern.test(value)) {
        styles[value as Square] = { backgroundImage: `linear-gradient(${tint}, ${tint})` };
      }
    }
  }
  return styles;
};

type PatternBoardExample = PatternExample & { readonly signal?: PrincipleSignal };
const getExamplePly = (example: PatternBoardExample) =>
  example.ply ?? (example.moveNumber - 1) * 2 + (example.fenBefore?.split(" ")[1] === "b" ? 2 : 1);

export const patternBetterEvidence = (
  example: Pick<PatternExample, "fenBefore" | "bestMoveSan" | "bestContinuation" | "playerColor">,
  step: number,
  displayedFen: string,
  includeOpponent: boolean,
) => {
  if (!example.fenBefore || !example.bestMoveSan) return null;
  const moves = [example.bestMoveSan, ...(example.bestContinuation ?? [])].slice(0, 3);
  const steps = replayLine(example.fenBefore, moves);
  const beforeFen = step <= 0 ? example.fenBefore : steps[step - 1]?.fen;
  const expectedFen = step < 0 ? example.fenBefore : steps[step]?.fen;
  if (
    !beforeFen ||
    expectedFen !== displayedFen ||
    (!includeOpponent && example.playerColor && beforeFen.split(" ")[1] !== example.playerColor)
  )
    return null;
  const move = applySanMove(beforeFen, moves[Math.max(0, step)]);
  if (!move) return null;
  const book = lookupBookMove(beforeFen, move.uci);
  return {
    book,
    evidence: {
      destination: step < 0 ? move.uci.slice(0, 2) : move.uci.slice(2, 4),
      motifs: book ? ["book"] : step <= 0 ? ["best"] : [],
      principleSignals: [],
    },
    positionFacts: step < 0 ? undefined : { fen: displayedFen, facts: collectTeachingFacts(beforeFen, [move.san]) },
    line: createVerifiedLine(displayedFen, moves.slice(step + 1), null),
  };
};

export const patternTemporaryMoveEvidence = (
  beforeFen: string | undefined,
  move: AppliedMove | undefined,
  grade: TemporaryMoveGrade | null,
  displayedFen: string,
) => {
  if (!beforeFen || move?.fen !== displayedFen || applySanMove(beforeFen, move.uci)?.fen !== move.fen)
    return { book: null, evidence: null };
  const book = lookupBookMove(beforeFen, move.uci);
  return { book, evidence: temporaryMoveEvidence(beforeFen, move, grade, Boolean(book)) };
};

export const patternPrincipleEvidence = (
  example: PatternBoardExample,
  displayedFen: string,
  visible: boolean,
  temporaryActive: boolean,
  settings: ReviewBoardSettings = DEFAULT_REVIEW_BOARD_SETTINGS,
) => {
  const evidence =
    example.signal && visible && !temporaryActive && displayedFen === example.fenBefore
      ? principleMarkers([example.signal])
      : { squareStyles: {}, arrows: [], description: "" };
  return {
    ...evidence,
    arrows: settings.showBestMoveArrow ? evidence.arrows : [],
    squareStyles: settings.showPositionMarkers ? evidence.squareStyles : {},
  };
};

export const patternPositionMarkers = (
  fen: string,
  selection: { fen: string; square: Square } | null,
  allMarkersFen: string | null,
  settings: ReviewBoardSettings,
) => {
  return boardPositionMarkers(
    fen,
    { ...settings, showAllPositionMarkers: settings.showAllPositionMarkers && allMarkersFen === fen },
    selection?.fen === fen ? selection.square : null,
  );
};

export const PatternEvidenceLesson = ({
  format,
  exactTimeControl,
  motif,
  description,
  habit,
  occurrences,
  affectedGames,
  examples,
  principleExample,
  onReview,
  splitFrom = "md",
  boardMaxWidth,
}: PatternEvidenceLessonProps) => {
  const navigate = useNavigate();
  const available: PatternBoardExample[] = (principleExample ? [principleExample] : examples).filter(
    (example) => example.fenBefore,
  );
  const [remembered, setRemembered] = useRememberedPageState(
    `pattern:${format}:${exactTimeControl ?? "all"}:${motif}`,
    { gameId: "", ply: 0, view: "happened", step: -1 },
  );
  const rememberedIndex = available.findIndex(
    (example) => example.gameId === remembered.gameId && getExamplePly(example) === remembered.ply,
  );
  const selected = Math.max(0, rememberedIndex);
  const view = rememberedIndex >= 0 && remembered.view === "better" ? "better" : "happened";
  const rememberedStep =
    rememberedIndex >= 0 && Number.isInteger(remembered.step) && remembered.step >= -1 && remembered.step <= 10_000
      ? remembered.step
      : -1;
  const setSelected = (index: number) => {
    const example = available[index];
    if (example) setRemembered({ gameId: example.gameId, ply: getExamplePly(example), view: "happened", step: -1 });
  };
  const updateLesson = (patch: { view?: "happened" | "better"; step?: number }) =>
    setRemembered((current) => {
      const example =
        available.find((item) => item.gameId === current.gameId && getExamplePly(item) === current.ply) ??
        available[selected];
      return { ...current, gameId: example?.gameId ?? "", ply: example ? getExamplePly(example) : 0, ...patch };
    });
  const setView = (view: "happened" | "better") => updateLesson({ view });
  const setLineStep = (step: number) => updateLesson({ step });
  const [selectedTemporarySquare, setSelectedTemporarySquare] = useState<Square | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<{ fen: string; square: Square } | null>(null);
  const [allMarkersFen, setAllMarkersFen] = useState<string | null>(available[0]?.fenBefore ?? null);
  const [preview, setPreview] = useState<{ key: string; line: VerifiedLine; step: number; playing: boolean } | null>(
    null,
  );
  const [nikolaCopy, setNikolaCopy] = useState<{ explanation: string; rule?: string } | null>(null);
  const [nikolaLoading, setNikolaLoading] = useState(false);
  const [nikolaError, setNikolaError] = useState<string | null>(null);
  const nikolaRequest = useRef<AbortController | null>(null);
  const [recordedAnalysis, setRecordedAnalysis] = useState<{ key: string; moves: AnalyzedMoveResult[] } | null>(null);
  const [recordedLoading, setRecordedLoading] = useState(false);
  const [recordedError, setRecordedError] = useState(false);
  const [recordedRetry, setRecordedRetry] = useState(0);
  const { settings: boardSettings, change: saveBoardSettings } = useBoardPreferences("patterns");
  const showOpponentAnalysis = boardSettings.showOpponentAnalysis;
  const changeBoardSettings = (settings: ReviewBoardSettingsPatch) => {
    if (settings.showAllPositionMarkers != null)
      setAllMarkersFen(settings.showAllPositionMarkers ? displayedFen : null);
    saveBoardSettings(settings);
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [boardWidth, setBoardWidth] = useState(340);
  const gutter = useEvaluationGutterWidth();

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;
    const measure = () => setBoardWidth(Math.max(1, Math.floor(element.getBoundingClientRect().width - gutter)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [gutter]);

  const example = available[Math.min(selected, Math.max(0, available.length - 1))];
  const exampleKey = `${example?.gameId}:${example?.ply}:${example?.fenBefore}:${example?.san}`;
  const recordedMoves = recordedAnalysis?.key === exampleKey ? recordedAnalysis.moves : [];
  const recordedLine = useMemo(
    () => (example && recordedAnalysis?.key === exampleKey ? recordedPatternLine(example, recordedAnalysis.moves) : []),
    [example, exampleKey, recordedAnalysis],
  );
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const nikolaGate = resolveNikolaAction(entitlementSnapshot, "pattern_explanation", example?.gameId);
  const entitlementCta = useEntitlementCta(nikolaGate);
  useEffect(() => {
    setNikolaCopy(null);
    setNikolaError(null);
    setNikolaLoading(false);
    return () => {
      nikolaRequest.current?.abort();
      nikolaRequest.current = null;
    };
  }, [example?.gameId, example?.moveNumber, example?.fenBefore, motif, format]);
  useEffect(() => {
    setRecordedAnalysis(null);
    setRecordedError(false);
    if (!example?.fenBefore) return;
    const controller = new AbortController();
    setRecordedLoading(true);
    void fetchGameAnalysis(example.gameId, AbortSignal.any([controller.signal, AbortSignal.timeout(20_000)]))
      .then((analysis) => {
        if (controller.signal.aborted) return;
        const line = recordedPatternLine(example, analysis.moves);
        setRecordedAnalysis({ key: exampleKey, moves: analysis.moves });
        setRecordedError(line.length === 0);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRecordedError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setRecordedLoading(false);
      });
    return () => controller.abort();
  }, [example, exampleKey, recordedRetry]);
  const generateNikolaRead = async () => {
    if (!example || nikolaLoading) return;
    const controller = new AbortController();
    nikolaRequest.current = controller;
    setNikolaLoading(true);
    setNikolaError(null);
    try {
      const response = await requestPatternExplanation(
        example,
        AbortSignal.any([controller.signal, AbortSignal.timeout(90_000)]),
      );
      if (nikolaRequest.current === controller)
        setNikolaCopy({ explanation: response.explanation, rule: response.rule });
    } catch {
      if (nikolaRequest.current === controller)
        setNikolaError(
          "Nikola could not finish this explanation. Your recorded evidence is still available. Please retry.",
        );
    } finally {
      if (nikolaRequest.current === controller) {
        setNikolaLoading(false);
        nikolaRequest.current = null;
      }
    }
  };
  const line = useMemo(
    () =>
      view === "happened"
        ? recordedLine
        : example?.bestMoveSan
          ? [example.bestMoveSan, ...(example.bestContinuation ?? [])].slice(0, 3)
          : [],
    [example, view, recordedLine],
  );
  const steps = useMemo(() => (example?.fenBefore ? replayLine(example.fenBefore, line) : []), [example, line]);
  const lineStep = Math.min(rememberedStep, steps.length - 1);
  const activeStep = lineStep >= 0 ? steps[lineStep] : null;
  const boardFen = activeStep?.fen ?? example?.fenBefore ?? "start";
  const temporaryOrigin = useMemo(
    () =>
      format === "rapid" &&
      example?.playerRating != null &&
      example.opponentRating != null &&
      example.playerColor &&
      boardFen !== "start"
        ? {
            id: `${format}:${example.gameId}:${example.moveNumber}:${selected}:${view}:${lineStep}`,
            fen: boardFen,
            format: "rapid" as const,
            playerColor: example.playerColor,
            playerRating: example.playerRating,
            opponentRating: example.opponentRating,
            verifiedLine:
              view === "better" && line.slice(lineStep + 1).length > 0
                ? createVerifiedLine(boardFen, line.slice(lineStep + 1), null)
                : null,
            recordedSan: lineStep < 0 ? example.san : null,
            verifiedAlternativeSan: lineStep < 0 ? example.bestMoveSan : null,
          }
        : null,
    [boardFen, example, format, line, lineStep, selected, view],
  );
  const temporaryPlay = useTemporaryPlay(
    temporaryOrigin,
    REVIEW_EVALUATION_DEPTH[boardSettings.evaluationPreset],
    boardSettings.moveSounds,
  );
  useEffect(() => setSelectedTemporarySquare(null), [temporaryOrigin?.id, temporaryPlay?.state]);
  const temporaryActive = temporaryPlay != null && temporaryPlay.state.moves.length > 0;
  const actualFen = temporaryActive ? temporaryPlay.state.fen : boardFen;
  const previewKey = `${exampleKey}:${view}:${lineStep}:${actualFen}:${boardSettings.evaluationPreset}`;
  const activePreview = preview?.key === previewKey ? preview : null;
  const previewSteps = useMemo(
    () => (activePreview ? replayLine(activePreview.line.fen, activePreview.line.moves) : []),
    [activePreview],
  );
  const displayedFen = activePreview ? (previewSteps[activePreview.step]?.fen ?? activePreview.line.fen) : actualFen;
  const { notice, notify } = useBoardNotice(`${exampleKey}:${displayedFen}`);
  const liveEvaluation = useProgressiveEvaluation({
    fen: displayedFen,
    enabled: Boolean(example) && (temporaryActive || Boolean(activePreview)),
    maxDepth: REVIEW_EVALUATION_DEPTH[boardSettings.evaluationPreset],
  });
  useEffect(() => {
    setSelectedMarker(null);
    setAllMarkersFen(null);
  }, [displayedFen]);
  useEffect(() => {
    if (!activePreview?.playing || activePreview.step >= previewSteps.length - 1) return;
    const timer = window.setTimeout(() => {
      setPreview((current) => (current === activePreview ? { ...current, step: current.step + 1 } : current));
      if (boardSettings.moveSounds) playChessMoveSound(previewSteps[activePreview.step + 1]?.san);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [activePreview, previewSteps, boardSettings.moveSounds]);
  if (!example) return null;

  const sourceEvidenceVisible =
    boardSettings.showOpponentAnalysis ||
    !example.playerColor ||
    example.fenBefore?.split(" ")[1] === example.playerColor;
  const showSourceEvidence =
    sourceEvidenceVisible &&
    view === "happened" &&
    lineStep < 0 &&
    !temporaryActive &&
    !activePreview &&
    displayedFen === example.fenBefore;
  const showTacticalSquares =
    boardSettings.showPositionMarkers && boardSettings.showTacticalMotifs && showSourceEvidence;
  const principleEvidence = patternPrincipleEvidence(
    example,
    displayedFen,
    boardSettings.showPrincipleSignals && showSourceEvidence,
    temporaryActive || Boolean(activePreview),
    boardSettings,
  );
  const precedingMove = displayedFen === example.fenBefore ? precedingPatternMove(displayedFen, recordedMoves) : null;
  const moveArrows: Arrow[] = !boardSettings.showBestMoveArrow
    ? []
    : activeStep
      ? [chessArrow(activeStep.from as Square, activeStep.to as Square, PATTERN_MOVE_ARROW_COLOR)]
      : precedingMove
        ? [chessArrow(precedingMove.from as Square, precedingMove.to as Square, PATTERN_MOVE_ARROW_COLOR)]
        : view === "happened"
          ? example.playedFrom && example.playedTo
            ? [chessArrow(example.playedFrom as Square, example.playedTo as Square, PATTERN_MOVE_ARROW_COLOR)]
            : []
          : example.bestMoveFrom && example.bestMoveTo
            ? [chessArrow(example.bestMoveFrom as Square, example.bestMoveTo as Square, PATTERN_MOVE_ARROW_COLOR)]
            : [];
  const arrows: Arrow[] = dedupeArrows([
    ...principleEvidence.arrows,
    ...(!temporaryActive && !activePreview ? moveArrows : []),
  ]);
  const baseSquareStyles: CustomSquareStyles = {
    ...(showTacticalSquares ? evidenceSquareStyles(example.evidence) : {}),
  };
  const squareStyles: CustomSquareStyles = { ...baseSquareStyles, ...principleEvidence.squareStyles };
  const lineScore = lineScoreForPlayer(
    lineStep,
    example.evalBeforeCp,
    example.bestContinuationScores ?? null,
    example.bestContinuationEvals ?? [],
  );
  const playerEval =
    view === "happened"
      ? evaluationForPlayer("after", example.evalBeforeCp, example.evalAfterCp)
      : (lineScore?.cp ?? null);
  const previousLineEval =
    lineStep <= 0
      ? null
      : (lineScoreForPlayer(
          lineStep - 1,
          example.evalBeforeCp,
          example.bestContinuationScores ?? null,
          example.bestContinuationEvals ?? [],
        )?.cp ?? null);
  const historicalScore =
    view === "happened"
      ? lineStep > 0
        ? null
        : evaluationScoreForPlayer(
            lineStep < 0 ? "before" : "after",
            example.evalBeforeCp,
            example.evalAfterCp,
            example.evalBeforeMate,
            example.evalAfterMate,
          )
      : lineScore;
  const historicalEvaluation = normalizeEvaluation(historicalScore, example.playerColor === "b" ? "b" : "w");
  const evaluation = temporaryActive || activePreview ? liveEvaluation.evaluation : historicalEvaluation;
  const canPlayTemporary = !activePreview && temporaryPlay != null && canPlayerMove(temporaryPlay.state);
  const selectedTargets =
    boardSettings.showLegalMoves && canPlayTemporary && selectedTemporarySquare
      ? legalTargetsFrom(displayedFen, selectedTemporarySquare)
      : [];
  const lastReply =
    temporaryPlay && temporaryPlay.state.moves.length >= 2 && temporaryPlay.state.moves.length % 2 === 0
      ? temporaryPlay.state.moves.at(-1)?.uci
      : undefined;
  const temporaryStyles = temporarySquareStyles(selectedTargets, lastReply, selectedTemporarySquare);
  const attemptMove = (source: Square, target: Square): boolean => {
    if (!canPlayTemporary) return false;
    const legal = legalTargetsFrom(displayedFen, source).find(({ square }) => square === target);
    if (!legal) {
      notify();
      return false;
    }
    setSelectedTemporarySquare(null);
    setSelectedMarker(null);
    setAllMarkersFen(null);
    void temporaryPlay.move(source, target);
    return true;
  };
  const onSquareClick = (square: Square): void => {
    setSelectedMarker((current) =>
      current?.fen === displayedFen && current.square === square ? null : { fen: displayedFen, square },
    );
    if (!canPlayTemporary) {
      setSelectedTemporarySquare(null);
      return;
    }
    if (!selectedTemporarySquare) {
      setSelectedTemporarySquare(legalTargetsFrom(displayedFen, square).length > 0 ? square : null);
      return;
    }
    const target = legalTargetsFrom(displayedFen, selectedTemporarySquare).find(
      (candidate) => candidate.square === square,
    );
    const source = selectedTemporarySquare;
    setSelectedTemporarySquare(null);
    if (target) {
      setSelectedMarker(null);
      setAllMarkersFen(null);
      void temporaryPlay.move(source, square, target.promotions[0]);
    } else if (square !== source) {
      if (legalTargetsFrom(displayedFen, square).length > 0) setSelectedTemporarySquare(square);
      else notify();
    }
  };
  const betterPlan = explainBetterPlan({
    fenBefore: example.fenBefore,
    bestMove: example.bestMoveSan ?? "the better move",
    reply: example.bestContinuation?.[0],
    payoff: example.bestContinuation?.[1],
    before: example.evalBeforeCp ?? null,
    actualAfter: evaluationForPlayer("after", example.evalBeforeCp, example.evalAfterCp),
    bestFinal:
      lineScoreForPlayer(
        steps.length - 1,
        example.evalBeforeCp,
        example.bestContinuationScores ?? null,
        example.bestContinuationEvals ?? [],
      )?.cp ?? null,
    opponent: example.opponent ?? "Your opponent",
    centipawnLoss: example.centipawnLoss,
    evidence: example.evidence,
  });

  const examplePly = patternExamplePly(example.ply);
  const activeMotif = formatMotif(motif);
  const motifDestination = example.playedTo && squarePattern.test(example.playedTo) ? example.playedTo : null;
  const orientation = example.playerColor === "b" ? "black" : "white";
  const sourceMove = recordedMoves.find((move) => move.fenBefore === example.fenBefore && move.san === example.san);
  const recordedMove =
    !temporaryActive && !activePreview && view === "happened"
      ? showSourceEvidence
        ? sourceMove
        : recordedMoves.find((move) => move.fenAfter === displayedFen && move.san === activeStep?.san)
      : null;
  const recordedUci = recordedMove ? applySanMove(recordedMove.fenBefore, recordedMove.san)?.uci : null;
  const recordedBook = recordedMove && recordedUci ? lookupBookMove(recordedMove.fenBefore, recordedUci) : null;
  const evidenceMove = recordedMove
    ? {
        ...recordedMove,
        playedFrom: recordedUci?.slice(0, 2) ?? recordedMove.playedFrom,
        playedTo: recordedUci?.slice(2, 4) ?? recordedMove.playedTo,
        isPlayerMove: recordedMove.isPlayerMove ?? recordedMove.fenBefore.split(" ")[1] === example.playerColor,
        motifs: deriveVerifiedBookMotifs(recordedMove.motifs, recordedBook),
        principleSignals:
          recordedMove === sourceMove && example.signal
            ? [
                ...recordedMove.principleSignals.filter((signal) => signal.principle !== example.signal!.principle),
                example.signal,
              ]
            : recordedMove.principleSignals,
      }
    : null;
  const sourceUci = example.fenBefore ? applySanMove(example.fenBefore, example.san)?.uci : null;
  const sourceBook = example.fenBefore && sourceUci ? lookupBookMove(example.fenBefore, sourceUci) : null;
  const sourceLine =
    example.fenBefore && example.bestMoveSan
      ? createVerifiedLine(
          example.fenBefore,
          [example.bestMoveSan, ...(example.bestContinuation ?? [])].slice(0, 3),
          null,
        )
      : null;
  const temporaryMoves = temporaryPlay?.state.moves ?? [];
  const playerMoveIndex = Math.floor((temporaryMoves.length - 1) / 2) * 2;
  const playerMove = temporaryMoves[playerMoveIndex];
  const playerBefore = playerMoveIndex > 0 ? temporaryMoves[playerMoveIndex - 1]?.fen : temporaryPlay?.state.origin.fen;
  const lastTemporaryMove = temporaryMoves.at(-1);
  const lastTemporaryBefore = temporaryMoves.length > 1 ? temporaryMoves.at(-2)?.fen : temporaryPlay?.state.origin.fen;
  const playerEvidence = patternTemporaryMoveEvidence(
    playerBefore,
    playerMove,
    temporaryPlay?.state.grade ?? null,
    displayedFen,
  );
  const replyEvidence = patternTemporaryMoveEvidence(lastTemporaryBefore, lastTemporaryMove, null, displayedFen);
  const temporaryBook = playerEvidence.book ?? (showOpponentAnalysis ? replyEvidence.book : null);
  const temporaryEvidence = playerEvidence.evidence ?? (showOpponentAnalysis ? replyEvidence.evidence : null);
  const betterEvidence =
    view === "better" && !temporaryActive && !activePreview
      ? patternBetterEvidence(example, lineStep, displayedFen, boardSettings.showOpponentAnalysis)
      : null;
  const precedingPlayer =
    playerMove && playerMove.fen !== displayedFen && lastTemporaryMove?.fen === displayedFen
      ? patternTemporaryMoveEvidence(playerBefore, playerMove, temporaryPlay?.state.grade ?? null, playerMove.fen)
      : null;
  const positionFacts =
    playerMove?.fen === displayedFen && playerBefore
      ? { fen: displayedFen, facts: collectTeachingFacts(playerBefore, [playerMove.san]) }
      : showOpponentAnalysis && lastTemporaryMove?.fen === displayedFen && lastTemporaryBefore
        ? { fen: displayedFen, facts: collectTeachingFacts(lastTemporaryBefore, [lastTemporaryMove.san]) }
        : undefined;
  const temporaryLine =
    temporaryPlay?.state.status === "ready" && temporaryPlay.state.replySource && temporaryMoves.length % 2 === 0
      ? createVerifiedLine(actualFen, temporaryPlay.state.continuation.slice(1, 4), null)
      : null;
  const evidenceLine = temporaryActive
    ? temporaryLine
    : evidenceMove?.bestMoveSan
      ? createVerifiedLine(
          evidenceMove.fenBefore,
          [evidenceMove.bestMoveSan, ...(evidenceMove.bestContinuation ?? [])].slice(0, 3),
          null,
        )
      : sourceLine;
  const playEvidenceLine = () => {
    if (evidenceLine) setPreview({ key: previewKey, line: evidenceLine, step: -1, playing: true });
  };
  const inspectPlayerMove = () => {
    const played = playerMove && playerBefore ? createVerifiedLine(playerBefore, [playerMove.uci], null) : null;
    if (played) setPreview({ key: previewKey, line: played, step: 0, playing: false });
  };
  const sourceItems: BoardEvidenceItem[] = [];
  const sourceDestination = motifDestination ?? sourceUci?.slice(2, 4);
  if (showSourceEvidence && sourceDestination) {
    const annotation = boardEvidenceMotifAnnotation([sourceBook ? "book" : motif]);
    if (boardSettings.showMotifs && annotation)
      sourceItems.push({
        id: `pattern:${motif}`,
        kind: "motif",
        square: sourceDestination,
        label: annotation.title,
        color: annotation.color,
        book: Boolean(sourceBook),
        description: sourceBook ? (bookMoveReference(sourceBook) ?? "Documented book move") : description,
        glyph: <MotifBadge kind={annotation.kind} label={annotation.label} color={annotation.color} />,
        disclosure: (
          <Typography variant="body2">
            {sourceBook ? <OpeningReference name={sourceBook.documentedIn?.name} /> : description}
          </Typography>
        ),
        playable: Boolean(sourceLine),
        onSelect: sourceLine ? playEvidenceLine : undefined,
        highlightSquares: Object.keys(evidenceSquareStyles(example.evidence)),
        highlightFen: example.fenBefore,
      });
    if (boardSettings.showPrincipleSignals && example.signal)
      sourceItems.push({
        id: `principle:${example.signal.principle}`,
        kind: "principle",
        square: sourceDestination,
        label: `${example.signal.status} principle: ${principlePresentation(example.signal.principle).name}`,
        color: "secondary.main",
        description: principlePresentation(example.signal.principle).cue,
        principleStatus: example.signal.status,
        glyph: <PrincipleGlyph principle={example.signal.principle} />,
        disclosure: <Typography variant="body2">{principlePresentation(example.signal.principle).cue}</Typography>,
        playable: Boolean(sourceLine),
        onSelect: sourceLine ? playEvidenceLine : undefined,
        highlightSquares: example.signal.anchors.map((anchor) => anchor.square),
        highlightFen: example.fenBefore,
      });
    if (sourceLine)
      sourceItems.push({
        id: "verified",
        kind: "verified",
        square: sourceDestination,
        label: "Verified line",
        color: "secondary.main",
        description: [example.bestMoveSan, ...(example.bestContinuation ?? [])].slice(0, 3).join(" "),
        glyph: <ReviewEvidenceGlyph kind="verified-line" />,
        disclosure: <Typography variant="body2">Inspect the verified continuation.</Typography>,
        playable: true,
        onSelect: playEvidenceLine,
      });
  }
  const displayedSteps = activePreview ? previewSteps : steps;
  const displayedStep = activePreview?.step ?? lineStep;
  const selectLineStep = (step: number) => {
    if (activePreview) setPreview({ ...activePreview, step, playing: false });
    else setLineStep(step);
    if (boardSettings.moveSounds && step >= 0) playChessMoveSound(displayedSteps[step]?.san);
  };
  return (
    <Box
      component="section"
      data-testid="pattern-evidence"
      data-game-id={example.gameId}
      data-ply={examplePly}
      data-fen={example.fenBefore}
      sx={{
        borderTop: `${layoutTokens.dividerBorder}px solid ${surfaceTokens.inverseDivider}`,
        bgcolor: "background.paper",
        color: "text.primary",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          borderBottom: layoutTokens.dividerBorder,
          borderColor: "divider",
        }}
      >
        <Box>
          <MicroLabel>{format.toUpperCase()} EVIDENCE</MicroLabel>
          <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography
              data-testid="active-pattern-label"
              sx={{
                fontSize: "caption.fontSize",
                fontWeight: "fontWeightBold",
                letterSpacing: typographyTokens.tracking.label,
              }}
            >
              ACTIVE PATTERN · {activeMotif.toUpperCase()}
            </Typography>
            {example.signal && sourceEvidenceVisible && boardSettings.showPrincipleSignals && (
              <PrincipleSignalBadge signal={example.signal} />
            )}
          </Box>
          <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: "caption.fontSize" }}>
            {occurrences} moments across {affectedGames} games.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "caption.fontSize",
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.label,
            }}
          >
            RED ARROW = PLAYED · GREEN ARROW = BETTER · GOLD = TARGET · PURPLE = PIN · BLUE = SUPPORT, CONTROL, OR
            FORCING SQUARE
          </Typography>
        </Box>
      </Box>
      <Box
        component="nav"
        aria-label="Pattern evidence navigation"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          event.stopPropagation();
          setSelected(Math.min(available.length - 1, Math.max(0, selected + (event.key === "ArrowRight" ? 1 : -1))));
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}
      >
        <IconButton aria-label="Previous evidence" disabled={selected === 0} onClick={() => setSelected(selected - 1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography aria-live="polite" sx={{ fontSize: "body2.fontSize" }}>
          Evidence {selected + 1} of {available.length}
        </Typography>
        <IconButton
          aria-label="Next evidence"
          disabled={selected >= available.length - 1}
          onClick={() => setSelected(selected + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <ReviewBoardSettingsDialog
        context="pattern"
        open={settingsOpen}
        settings={boardSettings}
        onChange={changeBoardSettings}
        onClose={() => setSettingsOpen(false)}
      />

      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", [splitFrom]: "minmax(0, 0.78fr) minmax(0, 1fr)" } }}
      >
        <Box
          ref={wrapperRef}
          sx={{
            minWidth: 0,
            width: "100%",
            maxWidth: boardMaxWidth ?? "none",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            borderBottom: { xs: layoutTokens.dividerBorder, [splitFrom]: 0 },
            borderRight: { [splitFrom]: layoutTokens.dividerBorder },
            borderColor: "divider",
          }}
        >
          <BoardFrame
            width={boardWidth}
            evaluation={
              boardSettings.showEvaluation
                ? {
                    evaluation,
                    orientation,
                    pending: (temporaryActive || Boolean(activePreview)) && liveEvaluation.pending,
                    label: activePreview ? "Verified continuation" : "from your perspective",
                  }
                : undefined
            }
          >
            {notice}
            <ChesslaBoardSurface
              settings={{
                ...boardSettings,
                showAllPositionMarkers: boardSettings.showAllPositionMarkers && allMarkersFen === displayedFen,
              }}
              relationshipFocus={selectedMarker}
              id={`pattern-${motif}-${selected}-${view}-${lineStep}`}
              position={displayedFen}
              orientation={orientation}
              width={boardWidth}
              animationDuration={0}
              allowDragging={canPlayTemporary}
              onPieceDrop={canPlayTemporary ? attemptMove : undefined}
              onSquareClick={onSquareClick}
              arrows={arrows}
              squareStyles={{ ...squareStyles, ...(!activePreview ? temporaryStyles : {}) }}
              evidence={(relationships) =>
                (!activePreview && temporaryActive) || displayedStep >= 0 ? (
                  betterEvidence ? (
                    <BoardEvidenceBadges
                      relationships={relationships}
                      move={null}
                      displayFen={displayedFen}
                      orientation={orientation}
                      showMotifs={boardSettings.showMotifs}
                      showTactics={boardSettings.showTacticalMotifs}
                      showPrinciples={boardSettings.showPrincipleSignals}
                      temporaryActive
                      temporaryEvidence={betterEvidence.evidence}
                      positionFacts={betterEvidence.positionFacts}
                      verifiedLineOverride={betterEvidence.line}
                      bookReference={bookMoveReference(betterEvidence.book)}
                      onPlayVerifiedLine={() => {
                        if (betterEvidence.line)
                          setPreview({ key: previewKey, line: betterEvidence.line, step: -1, playing: true });
                      }}
                    />
                  ) : (!activePreview || playerMove?.fen === displayedFen) && (evidenceMove || temporaryActive) ? (
                    <BoardEvidenceBadges
                      relationships={relationships}
                      move={evidenceMove}
                      displayFen={displayedFen}
                      orientation={orientation}
                      showMotifs={boardSettings.showMotifs}
                      showTactics={boardSettings.showTacticalMotifs}
                      showPrinciples={boardSettings.showPrincipleSignals}
                      includeOpponentEvidence={boardSettings.showOpponentAnalysis}
                      reviewDecision={showSourceEvidence}
                      temporaryActive={temporaryActive}
                      temporaryEvidence={temporaryEvidence}
                      positionFacts={positionFacts}
                      precedingMove={
                        precedingPlayer?.evidence && playerMove
                          ? {
                              fen: playerMove.fen,
                              displayFen: displayedFen,
                              evidence: precedingPlayer.evidence,
                              bookReference: bookMoveReference(precedingPlayer.book) ?? undefined,
                              facts: playerBefore ? collectTeachingFacts(playerBefore, [playerMove.san]) : undefined,
                            }
                          : undefined
                      }
                      verifiedLineOverride={
                        temporaryActive ? temporaryLine : showSourceEvidence ? sourceLine : undefined
                      }
                      onPlayVerifiedLine={playEvidenceLine}
                      bookReference={bookMoveReference(temporaryActive ? temporaryBook : recordedBook)}
                    />
                  ) : showSourceEvidence ? (
                    <BoardEvidenceWheel
                      items={mergeRelationshipEvidence(sourceItems, relationships)}
                      displayFen={displayedFen}
                      orientation={orientation}
                      bookOnly={Boolean(sourceBook)}
                    />
                  ) : (
                    <BoardEvidenceWheel items={relationships} displayFen={displayedFen} orientation={orientation} />
                  )
                ) : (
                  <BoardEvidenceWheel items={relationships} displayFen={displayedFen} orientation={orientation} />
                )
              }
            />
          </BoardFrame>
          <BoardMoveControls
            label={
              activePreview
                ? "Verified continuation"
                : temporaryActive
                  ? "Your idea"
                  : view === "happened"
                    ? "Recorded moves"
                    : "Verified line"
            }
            position={temporaryActive && !activePreview ? 0 : displayedStep + 1}
            count={temporaryActive && !activePreview ? 0 : displayedSteps.length}
            disabled={temporaryActive && !activePreview}
            onNavigate={(position) => selectLineStep(position - 1)}
            onRestore={() => {
              if (activePreview) setPreview(null);
              else if (temporaryActive) temporaryPlay?.reset();
              else selectLineStep(-1);
            }}
            onTogglePlay={
              activePreview
                ? () =>
                    setPreview({
                      ...activePreview,
                      step: activePreview.step >= previewSteps.length - 1 ? -1 : activePreview.step,
                      playing: activePreview.step >= previewSteps.length - 1 || !activePreview.playing,
                    })
                : undefined
            }
            playing={Boolean(activePreview?.playing && activePreview.step < previewSteps.length - 1)}
            onSettings={() => setSettingsOpen(true)}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, p: { xs: 2, sm: 2.5 } }}>
          <TemporaryPlayRail controller={temporaryPlay} />
          {!activePreview && temporaryActive && playerMove && (
            <Button onClick={inspectPlayerMove} variant="outlined" sx={{ mb: 1, borderRadius: 0 }}>
              INSPECT YOUR MOVE
            </Button>
          )}
          {!activePreview && temporaryLine && (
            <Button onClick={playEvidenceLine} variant="outlined" sx={{ mb: 1, borderRadius: 0 }}>
              PLAY VERIFIED CONTINUATION
            </Button>
          )}
          <MicroLabel>Recorded moment</MicroLabel>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: { xs: "h6.fontSize", sm: "h5.fontSize" },
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.heading,
            }}
          >
            {example.moveNumber}. {example.san}
          </Typography>
          <Button
            onClick={() => onReview(example)}
            sx={{
              minHeight: layoutTokens.controlHeight,
              mt: 0.5,
              p: 0,
              justifyContent: "flex-start",
              borderRadius: 0,
              color: "text.secondary",
              fontSize: "caption.fontSize",
              textAlign: "left",
            }}
          >
            YOUR GAME AGAINST {(example.opponent ?? "OPPONENT").toUpperCase()} · PLY {examplePly}
            {example.opening ? ` · ${example.opening}` : ""}
          </Button>
          {example.roleReason && (
            <Typography
              sx={{
                mt: 0.75,
                color: "text.secondary",
                fontSize: "caption.fontSize",
                lineHeight: typographyTokens.lineHeight.body,
              }}
            >
              {example.roleReason}
            </Typography>
          )}

          {!temporaryActive && (
            <Box sx={{ display: "flex", mt: 1.5, borderBottom: layoutTokens.dividerBorder, borderColor: "divider" }}>
              {(["happened", "better"] as const).map((item) => (
                <Button
                  key={item}
                  onClick={() => {
                    setView(item);
                    setLineStep(-1);
                  }}
                  aria-pressed={view === item}
                  sx={{
                    borderRadius: 0,
                    borderBottom: layoutTokens.accentBorder,
                    borderColor: view === item ? "primary.main" : "transparent",
                    color: view === item ? "text.primary" : "text.secondary",
                    fontSize: "caption.fontSize",
                    px: 1,
                    py: 0.75,
                  }}
                >
                  {item === "happened" ? "WHAT HAPPENED" : "BETTER IDEA"}
                </Button>
              ))}
            </Box>
          )}

          {view === "happened" ? (
            <Box sx={{ mt: 1.5 }}>
              <MicroLabel>REPEATED DECISION PROBLEM</MicroLabel>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "body1.fontSize",
                  fontWeight: "fontWeightBold",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                You chose {example.san}.{" "}
                {evidenceSwingText(
                  example.centipawnLoss,
                  evaluationForPlayer("before", example.evalBeforeCp, example.evalAfterCp),
                  evaluationForPlayer("after", example.evalBeforeCp, example.evalAfterCp),
                )}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: "text.secondary",
                  fontSize: "body2.fontSize",
                  lineHeight: typographyTokens.lineHeight.body,
                  whiteSpace: "pre-line",
                }}
              >
                {nikolaCopy?.explanation ?? description}
              </Typography>
              {!nikolaCopy && (
                <Button
                  onClick={() => {
                    if (nikolaGate.allowed) void generateNikolaRead();
                    else if (entitlementCta) void navigate(entitlementCta.href);
                  }}
                  disabled={nikolaLoading || (!nikolaGate.allowed && !entitlementCta)}
                  variant="outlined"
                  sx={{ mt: 1, borderRadius: 0, fontSize: "caption.fontSize" }}
                >
                  {nikolaLoading ? (
                    "NIKOLA · VERIFYING EVIDENCE · WRITING YOUR READ…"
                  ) : nikolaGate.allowed ? (
                    <span>
                      {nikolaError ? "RETRY NIKOLA" : "ASK NIKOLA ABOUT THIS"}
                      <small style={{ display: "block" }}>up to {nikolaGate.estimate} credits</small>
                    </span>
                  ) : (
                    <span>
                      {entitlementCta?.label}
                      <small style={{ display: "block" }}>{entitlementCta?.detail}</small>
                    </span>
                  )}
                </Button>
              )}
              {nikolaError && (
                <Typography role="alert" sx={{ mt: 1, color: "error.main", fontSize: "caption.fontSize" }}>
                  {nikolaError}
                </Typography>
              )}
              <Box
                sx={{
                  mt: 1.5,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  border: layoutTokens.dividerBorder,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ p: 1.25, borderRight: layoutTokens.dividerBorder, borderColor: "divider" }}>
                  <MicroLabel>Before</MicroLabel>
                  <Typography sx={{ mt: 0.25, fontSize: "body1.fontSize", fontWeight: "fontWeightBold" }}>
                    {scoreText(
                      evaluationScoreForPlayer(
                        "before",
                        example.evalBeforeCp,
                        example.evalAfterCp,
                        example.evalBeforeMate,
                        example.evalAfterMate,
                      ),
                    )}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.25 }}>
                  <MicroLabel>After {example.san}</MicroLabel>
                  <Typography
                    sx={{ mt: 0.25, color: "primary.main", fontSize: "body1.fontSize", fontWeight: "fontWeightBold" }}
                  >
                    {scoreText(
                      evaluationScoreForPlayer(
                        "after",
                        example.evalBeforeCp,
                        example.evalAfterCp,
                        example.evalBeforeMate,
                        example.evalAfterMate,
                      ),
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : !temporaryActive ? (
            <Box sx={{ mt: 1.5 }}>
              <MicroLabel>Why this is better</MicroLabel>
              <Typography sx={{ mt: 0.5, fontSize: "body1.fontSize", fontWeight: "fontWeightBold" }}>
                {example.bestMoveSan ? `Start with ${example.bestMoveSan}.` : "Stop and compare one calmer candidate."}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: "text.secondary",
                  fontSize: "body2.fontSize",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                {betterPlan}
              </Typography>
            </Box>
          ) : null}
          {view === "happened" && recordedLoading && (
            <Typography role="status" sx={{ mt: 1, fontSize: "caption.fontSize" }}>
              Loading recorded moves…
            </Typography>
          )}
          {view === "happened" && recordedError && (
            <Button onClick={() => setRecordedRetry((value) => value + 1)} sx={{ mt: 1 }}>
              Recorded moves unavailable · Retry
            </Button>
          )}
          {(activePreview != null || !temporaryActive) && displayedSteps.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <MicroLabel>
                {!activePreview && view === "happened" ? "WHAT HAPPENED · RECORDED MOVES" : "Nikola · verified line"}
              </MicroLabel>
              <Box
                sx={{
                  mt: 0.5,
                  display: "flex",
                  flexWrap: "wrap",
                  border: layoutTokens.dividerBorder,
                  borderColor: "divider",
                }}
              >
                <Button
                  onClick={() => selectLineStep(-1)}
                  aria-pressed={displayedStep < 0}
                  sx={{
                    borderRadius: 0,
                    borderRight: layoutTokens.dividerBorder,
                    borderColor: "divider",
                    color: displayedStep < 0 ? "primary.main" : "text.secondary",
                    fontSize: "caption.fontSize",
                  }}
                >
                  START
                </Button>
                {displayedSteps.map((step, index) => (
                  <Button
                    key={`${index}:${step.san}`}
                    onClick={() => selectLineStep(index)}
                    aria-pressed={displayedStep === index}
                    sx={{
                      borderRadius: 0,
                      borderRight: index < displayedSteps.length - 1 ? layoutTokens.dividerBorder : 0,
                      borderColor: "divider",
                      bgcolor: displayedStep === index ? "primary.main" : "transparent",
                      color: displayedStep === index ? "background.default" : "text.primary",
                      fontSize: "caption.fontSize",
                      fontWeight: "fontWeightBold",
                    }}
                  >
                    {index + 1}. {step.san}
                  </Button>
                ))}
              </Box>
              {!activePreview && activeStep && view === "better" && (
                <Box
                  data-testid="line-explanation"
                  sx={{
                    mt: 1,
                    borderLeft: layoutTokens.accentBorder,
                    borderColor: lineStep % 2 === 1 ? "warning.main" : "secondary.main",
                    pl: 1.25,
                    py: 0.25,
                  }}
                >
                  <MicroLabel>{lineStep % 2 === 1 ? "Likely reply" : "Why this move"}</MicroLabel>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: "body2.fontSize",
                      fontWeight: "fontWeightBold",
                      lineHeight: typographyTokens.lineHeight.body,
                    }}
                  >
                    {explainLineStep(
                      activeStep.san,
                      lineStep,
                      playerEval,
                      previousLineEval,
                      example.opponent ?? "Opponent",
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ mt: "auto", pt: 2 }}>
            <Typography
              sx={{
                borderLeft: layoutTokens.accentBorder,
                borderColor: "primary.main",
                pl: 1,
                fontSize: "caption.fontSize",
                fontWeight: "fontWeightBold",
                lineHeight: typographyTokens.lineHeight.body,
              }}
            >
              RECOGNITION CUE: {nikolaCopy?.rule ?? habit}
            </Typography>
            <Button
              onClick={() => onReview(example)}
              sx={{ mt: 1, borderRadius: 0, fontSize: "caption.fontSize", px: 0 }}
            >
              REVIEW THIS MOMENT →
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
